/**
 * Click-to-call helper.
 *
 * Three delivery paths (best-effort, automatic):
 *   1. Same-tab CustomEvent on window — if the FloatingDialer (always
 *      mounted in Layout) or /softphone is present in the SAME tab,
 *      we dispatch a `voice-gateway-dial` window event which the
 *      SoftphoneProvider listens to. This is the fast path inside the
 *      authenticated app.
 *   2. BroadcastChannel "voice-gateway-dial" — if the Softphone is open
 *      in ANOTHER tab AND registered, it picks up the message and
 *      dials in-place (no new window/popup).
 *   3. Popup fallback: open `/softphone?dial=<E164>` in a popup window
 *      when neither (1) nor (2) ack within ~250 ms (e.g. user is on a
 *      logged-out page).
 *
 * The Softphone provider acknowledges receipt back via the same channel
 * (`{type:'dial-ack'}`), so the caller side knows the popup isn't needed.
 */

const CHANNEL_NAME = 'voice-gateway-dial';
const SAME_TAB_EVENT = 'voice-gateway-dial';

/** Strip spaces, dashes, parens, dots — keep leading + and digits. */
export const normalizeNumber = (raw) => {
    if (!raw) return '';
    const t = String(raw).trim();
    const plus = t.startsWith('+') ? '+' : '';
    return plus + t.replace(/[^\d]/g, '');
};

/**
 * Trigger a click-to-call.
 *
 * @param {string} number  - E.164 or local format (will be normalized).
 * @param {object} [opts]
 * @param {string} [opts.contactName]  - optional caller-id label.
 */
export const triggerCall = async (number, opts = {}) => {
    const target = normalizeNumber(number);
    if (!target) throw new Error('Invalid phone number');

    let acked = false;
    let bc;

    // Same-tab path: listen for ack BEFORE dispatching
    const onSameTabAck = (e) => {
        if (e?.detail?.type === 'dial-ack' && e.detail.target === target) acked = true;
    };
    try { window.addEventListener(SAME_TAB_EVENT + '-ack', onSameTabAck); } catch (_) { /* noop */ }

    try {
        // (1) Same-tab dispatch — FloatingDialer / SoftphoneProvider in this tab picks it up
        try {
            window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, {
                detail: { type: 'dial', target, contactName: opts.contactName || null },
            }));
        } catch (_) { /* noop */ }

        // (2) Cross-tab BroadcastChannel
        if (typeof BroadcastChannel !== 'undefined') {
            bc = new BroadcastChannel(CHANNEL_NAME);
            const onMsg = (e) => {
                if (e?.data?.type === 'dial-ack' && e.data.target === target) {
                    acked = true;
                }
            };
            bc.addEventListener('message', onMsg);
            bc.postMessage({
                type: 'dial',
                target,
                contactName: opts.contactName || null,
                ts: Date.now(),
            });
            // Wait briefly for an ack from either same-tab or cross-tab listener
            await new Promise((res) => setTimeout(res, 280));
            bc.removeEventListener('message', onMsg);
        } else {
            // No BC support — still give same-tab path a tick to ack
            await new Promise((res) => setTimeout(res, 50));
        }
    } catch (_) {
        // BroadcastChannel not supported / errored — fall through to popup
    } finally {
        try { bc?.close(); } catch (_) { /* noop */ }
        try { window.removeEventListener(SAME_TAB_EVENT + '-ack', onSameTabAck); } catch (_) { /* noop */ }
    }

    if (acked) {
        return { delivered: 'in-context', target };
    }

    // Fallback — open the softphone in a new popup with auto-dial param
    const url = `/softphone?dial=${encodeURIComponent(target)}`
        + (opts.contactName ? `&name=${encodeURIComponent(opts.contactName)}` : '');
    const features = 'popup=yes,width=420,height=720,menubar=no,toolbar=no,location=no';
    const w = window.open(url, 'voice-gateway-softphone', features);
    if (!w) {
        // Popup blocked — open in-place tab
        window.open(url, '_blank');
    } else {
        try { w.focus(); } catch (_) { /* ignore */ }
    }
    return { delivered: 'popup', target };
};

export const DIAL_CHANNEL = CHANNEL_NAME;
